import { ActivatedRouteSnapshot, CanDeactivateFn, RouterStateSnapshot } from '@angular/router';
import { PagePbDealDetailComponent } from '../pages';
import { inject } from '@angular/core';
import { ConfirmationDialogService } from '../../../modules/fwcore';
import { map, take, tap } from 'rxjs/operators';
import { DialogResult } from '@progress/kendo-angular-dialog';

import { RequestDealDraftReset, ResponseDealDraftReset } from 'src/app/features/prob/models/messages/deal-draft-reset-messages';
import { DealsService } from 'src/app/features/prob/services';
import { RequestDealLocking, ResponseDealLocking } from '../models';

export const CanDeactivateDealCompGuard: CanDeactivateFn<PagePbDealDetailComponent> = (
  component: PagePbDealDetailComponent,
  currentRoute: ActivatedRouteSnapshot,
  currentState: RouterStateSnapshot,
  nextState: RouterStateSnapshot
) => {

  // Always inject first before any early returns
  const dlg = inject(ConfirmationDialogService);
  const dataService = inject(DealsService);

  const dealId = component.currentDealId;

  if (!dealId)
    return true;

  // Helper - fire-and-forget unlock. Stale lock takeover (20 mins) on the server
  // is the safety net if this request never lands.
  const requestUnlock = (id: string) => {
    const req: RequestDealLocking = { dealId: id, lock: false };
    dataService.setData$<RequestDealLocking, ResponseDealLocking>(req, dataService.endPointDealLocking)
      .pipe(take(1))
      .subscribe();
  };

  // IMPORTANT:
  // Save As intentionally leaves the current scenario.
  // Do not show the "User Changes" popup for that navigation.
  if (component.consumeDeactivateBypass()) {
    requestUnlock(dealId);
    return true;
  }

  // Normal navigation continues from here
  const isLive = component.isLive();
  const isDirty = component.isDirty();

  

  if (isLive && !isDirty) {
    requestUnlock(dealId);
    return true;
  }

  const message = isLive === false
    ? 'Do you wish to exit and lose your changes?'
    : 'You have changes or are missing required data. Do you wish to exit and lose your changes?';

  const title = isLive === false ? 'User Changes' : 'Changes';

  return dlg.open(title, message)
    .pipe(
      map((result: any) => result.value ?? true),
      tap((confirmed: boolean) => {

        if (confirmed && dealId) {

          const req: RequestDealDraftReset = { dealIds: [dealId] };

          dataService.setData$<RequestDealDraftReset, ResponseDealDraftReset>(req, dataService.endPointDealDraftReset)
            .pipe(take(1))
            .subscribe();

          // Release the lock - user is leaving the deal
          requestUnlock(dealId);
        }
      })
    );
};
